import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AttachMoneyOutlined as CostIcon,
  BusinessOutlined as CompanyIcon,
  CloudOutlined as ModelIcon,
  ContentCopyOutlined as CopyIcon,
  CorporateFareOutlined as OrgIcon,
  ExtensionOutlined as FeatureIcon,
  LanguageOutlined as WhitelabelIcon,
  PeopleOutlined as UserIcon,
  TrendingUpOutlined as TrendingIcon,
} from "@mui/icons-material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import axios from "axios";
import { hostname, parseError } from "../../utils";

interface CostManagementProps {
  getToken: () => Promise<string>;
}

interface CostData {
  period: {
    start: string;
    end: string;
    days: number;
    labels: string[];
  };
  summary: {
    totalCostUsd: number;
    totalTokens: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalRequests: number;
    avgCostPerDay: number;
    avgRequestsPerDay: number;
  };
  timeSeries: {
    costsUsd: number[];
    requests: number[];
  };
  breakdown: {
    whitelabels: WhitelabelCost[];
    organizations: OrganizationCost[];
    companies: CompanyCost[];
    users: UserCost[];
    models: ModelCost[];
    features: FeatureCost[];
  };
}

interface WhitelabelCost {
  id: string | null;
  name: string;
  domain: string | null;
  costUsd: number;
  tokens: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

interface OrganizationCost {
  id: string;
  name: string;
  costUsd: number;
  tokens: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
  companies: number;
}

interface CompanyCost {
  id: string;
  name: string;
  organizationId: string | null;
  costUsd: number;
  tokens: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

interface UserCost {
  uid: string;
  email: string;
  displayName: string | null;
  costUsd: number;
  tokens: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

interface ModelCost {
  model: string;
  label: string;
  provider: string;
  costUsd: number;
  tokens: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

interface FeatureCost {
  feature: string;
  costUsd: number;
  tokens: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

type TabValue = "whitelabels" | "organizations" | "companies" | "users" | "models" | "features";

const MetricCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string | number;
  subtitle?: string;
  color?: string;
}> = ({ icon, title, value, subtitle, color = "primary.main" }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ height: "100%", display: "flex", alignItems: "center" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

const formatUsd = (usd: number): string => {
  if (usd >= 1) {
    return `$${usd.toFixed(2)}`;
  } else if (usd >= 0.01) {
    return `$${usd.toFixed(2)}`;
  } else if (usd >= 0.001) {
    return `$${usd.toFixed(3)}`;
  } else if (usd > 0) {
    return `$${usd.toFixed(4)}`;
  }
  return "$0.00";
};

const CostManagement: React.FC<CostManagementProps> = ({ getToken }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CostData | null>(null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState<TabValue>("whitelabels");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchCosts = async (numDays: number) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/admin/costs?days=${numDays}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCosts(days);
  }, [days]);

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading && !data) {
    return (
      <Box sx={{ width: "100%" }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: "center" }} color="text.secondary">
          Loading cost data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="warning">No cost data available</Alert>;
  }

  const renderWhitelabelsTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Whitelabel</TableCell>
            <TableCell>Domain</TableCell>
            <TableCell align="right">Cost (USD)</TableCell>
            <TableCell align="right">Input</TableCell>
            <TableCell align="right">Output</TableCell>
            <TableCell align="right">Requests</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.breakdown.whitelabels.map((wl, index) => (
            <TableRow key={wl.id || `main-${index}`} hover>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">{wl.name}</Typography>
                  {wl.id && (
                    <Tooltip title={copiedId === wl.id ? "Copied!" : "Copy ID"}>
                      <IconButton size="small" onClick={() => handleCopyId(wl.id!)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {wl.domain || "-"}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Chip label={formatUsd(wl.costUsd)} size="small" color="primary" variant="outlined" />
              </TableCell>
              <TableCell align="right">{formatNumber(wl.inputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(wl.outputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(wl.requests)}</TableCell>
            </TableRow>
          ))}
          {data.breakdown.whitelabels.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">No whitelabel data</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderOrganizationsTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Organization</TableCell>
            <TableCell align="right">Companies</TableCell>
            <TableCell align="right">Cost (USD)</TableCell>
            <TableCell align="right">Input</TableCell>
            <TableCell align="right">Output</TableCell>
            <TableCell align="right">Requests</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.breakdown.organizations.map((org) => (
            <TableRow key={org.id} hover>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">{org.name}</Typography>
                  <Tooltip title={copiedId === org.id ? "Copied!" : "Copy ID"}>
                    <IconButton size="small" onClick={() => handleCopyId(org.id)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right">{org.companies}</TableCell>
              <TableCell align="right">
                <Chip label={formatUsd(org.costUsd)} size="small" color="primary" variant="outlined" />
              </TableCell>
              <TableCell align="right">{formatNumber(org.inputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(org.outputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(org.requests)}</TableCell>
            </TableRow>
          ))}
          {data.breakdown.organizations.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">No organization data</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCompaniesTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Company</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell align="right">Cost (USD)</TableCell>
            <TableCell align="right">Input</TableCell>
            <TableCell align="right">Output</TableCell>
            <TableCell align="right">Requests</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.breakdown.companies.map((company) => (
            <TableRow key={company.id} hover>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">{company.name}</Typography>
                  <Tooltip title={copiedId === company.id ? "Copied!" : "Copy ID"}>
                    <IconButton size="small" onClick={() => handleCopyId(company.id)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                {company.organizationId ? (
                  <Chip label="Org" size="small" variant="outlined" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    -
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Chip label={formatUsd(company.costUsd)} size="small" color="primary" variant="outlined" />
              </TableCell>
              <TableCell align="right">{formatNumber(company.inputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(company.outputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(company.requests)}</TableCell>
            </TableRow>
          ))}
          {data.breakdown.companies.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">No company data</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderUsersTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Email</TableCell>
            <TableCell align="right">Cost (USD)</TableCell>
            <TableCell align="right">Input</TableCell>
            <TableCell align="right">Output</TableCell>
            <TableCell align="right">Requests</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.breakdown.users.map((user) => (
            <TableRow key={user.uid} hover>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">{user.displayName || "(No name)"}</Typography>
                  <Tooltip title={copiedId === user.uid ? "Copied!" : "Copy UID"}>
                    <IconButton size="small" onClick={() => handleCopyId(user.uid)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Chip label={formatUsd(user.costUsd)} size="small" color="primary" variant="outlined" />
              </TableCell>
              <TableCell align="right">{formatNumber(user.inputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(user.outputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(user.requests)}</TableCell>
            </TableRow>
          ))}
          {data.breakdown.users.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">No user data</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderModelsTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Model</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell align="right">Cost (USD)</TableCell>
            <TableCell align="right">Input</TableCell>
            <TableCell align="right">Output</TableCell>
            <TableCell align="right">Requests</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.breakdown.models.map((model) => (
            <TableRow key={model.model} hover>
              <TableCell>
                <Typography variant="body2">{model.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {model.model}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip label={model.provider} size="small" variant="outlined" />
              </TableCell>
              <TableCell align="right">
                <Chip label={formatUsd(model.costUsd)} size="small" color="primary" variant="outlined" />
              </TableCell>
              <TableCell align="right">{formatNumber(model.inputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(model.outputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(model.requests)}</TableCell>
            </TableRow>
          ))}
          {data.breakdown.models.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">No model data</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderFeaturesTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Feature</TableCell>
            <TableCell align="right">Cost (USD)</TableCell>
            <TableCell align="right">Input</TableCell>
            <TableCell align="right">Output</TableCell>
            <TableCell align="right">Requests</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.breakdown.features.map((feature) => (
            <TableRow key={feature.feature} hover>
              <TableCell>
                <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                  {feature.feature.replace(/_/g, " ")}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Chip label={formatUsd(feature.costUsd)} size="small" color="primary" variant="outlined" />
              </TableCell>
              <TableCell align="right">{formatNumber(feature.inputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(feature.outputTokens)}</TableCell>
              <TableCell align="right">{formatNumber(feature.requests)}</TableCell>
            </TableRow>
          ))}
          {data.breakdown.features.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography color="text.secondary">No feature data</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderActiveTable = () => {
    switch (activeTab) {
      case "whitelabels":
        return renderWhitelabelsTable();
      case "organizations":
        return renderOrganizationsTable();
      case "companies":
        return renderCompaniesTable();
      case "users":
        return renderUsersTable();
      case "models":
        return renderModelsTable();
      case "features":
        return renderFeaturesTable();
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Header with Timeline Filter */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Platform Costs
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Period</InputLabel>
          <Select value={days} label="Time Period" onChange={(e) => setDays(e.target.value as number)}>
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={14}>Last 14 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={60}>Last 60 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
            <MenuItem value={180}>Last 180 days</MenuItem>
            <MenuItem value={365}>Last 365 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard icon={<CostIcon />} title="Total Cost (USD)" value={formatUsd(data.summary.totalCostUsd)} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard icon={<TrendingIcon />} title="Avg Cost/Day" value={formatUsd(data.summary.avgCostPerDay)} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard icon={<ModelIcon />} title="Input Tokens" value={formatNumber(data.summary.totalInputTokens)} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard icon={<ModelIcon />} title="Output Tokens" value={formatNumber(data.summary.totalOutputTokens)} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard icon={<CostIcon />} title="Total Requests" value={formatNumber(data.summary.totalRequests)} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard
            icon={<TrendingIcon />}
            title="Requests/Day"
            value={formatNumber(Math.round(data.summary.avgRequestsPerDay))}
          />
        </Grid>
      </Grid>

      {/* Time Series Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Daily Cost (USD)
              </Typography>
              {data.timeSeries.costsUsd.length > 0 ? (
                <LineChart
                  xAxis={[
                    {
                      data: data.period.labels.map((_, i) => i),
                      scaleType: "point",
                      valueFormatter: (value) => data.period.labels[value] || "",
                    },
                  ]}
                  series={[
                    {
                      data: data.timeSeries.costsUsd,
                      label: "Cost ($)",
                      color: "#1976d2",
                      area: true,
                      valueFormatter: (value) => `$${value?.toFixed(2) || "0.00"}`,
                    },
                  ]}
                  height={250}
                  margin={{ left: 60, right: 20, top: 20, bottom: 30 }}
                />
              ) : (
                <Box sx={{ height: 250, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Daily Requests
              </Typography>
              {data.timeSeries.requests.length > 0 ? (
                <BarChart
                  xAxis={[
                    {
                      data: data.period.labels,
                      scaleType: "band",
                    },
                  ]}
                  series={[
                    {
                      data: data.timeSeries.requests,
                      label: "Requests",
                      color: "#2e7d32",
                    },
                  ]}
                  height={250}
                  margin={{ left: 50, right: 20, top: 20, bottom: 30 }}
                />
              ) : (
                <Box sx={{ height: 250, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Breakdown Tabs */}
      <Card variant="outlined">
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab icon={<WhitelabelIcon />} iconPosition="start" label="Whitelabels" value="whitelabels" />
            <Tab icon={<OrgIcon />} iconPosition="start" label="Organizations" value="organizations" />
            <Tab icon={<CompanyIcon />} iconPosition="start" label="Companies" value="companies" />
            <Tab icon={<UserIcon />} iconPosition="start" label="Users" value="users" />
            <Tab icon={<ModelIcon />} iconPosition="start" label="Models" value="models" />
            <Tab icon={<FeatureIcon />} iconPosition="start" label="Features" value="features" />
          </Tabs>
        </Box>
        <CardContent>{renderActiveTable()}</CardContent>
      </Card>

    </Box>
  );
};

export default CostManagement;
